import React from 'react'
import { View } from 'react-native'
import { Text } from '@src/components/base'
import { NotificationInboxPresentation } from '@src/components/notifications/hooks/useNotificationInboxPresentation'
import { StyleService, useStyleSheet } from '@src/style/service'
import { ImageStyle, LoadingFastImage } from '@src/components/LoadingFastImage'
import { UiIcon } from '@src/types'
import { NotificationSenderAvatar } from './NotificationSenderAvatar'
import { getNotificationIconName } from './getNotificationIconName'

const AVATAR_SIZE = 40

interface NotificationContentRecord {
  content: string
  icon?: UiIcon | null
  imageUrl?: string | null
  id: string
  title?: string | null
}

interface NotificationContentProps {
  notification: NotificationContentRecord
  presentation: NotificationInboxPresentation
  showTimestamp?: boolean
  showUnreadDot?: boolean
  timestamp?: string
  useReadStateColors?: boolean
}

export const NotificationContent = ({
  notification,
  presentation,
  timestamp,
  showTimestamp = false,
  showUnreadDot = false,
  useReadStateColors = false,
}: NotificationContentProps) => {
  const styles = useStyleSheet(themedStyles)
  const imageUrl = notification.imageUrl || undefined
  const shouldShowTimestamp = showTimestamp && !!timestamp
  const hasImage = !!imageUrl
  const iconName = getNotificationIconName(notification.icon)
  const isUnreadNotificationContent = useReadStateColors && presentation.isUnread
  let senderLabelStyle = null

  if (useReadStateColors) {
    senderLabelStyle = isUnreadNotificationContent
      ? styles.senderLabelUnread
      : styles.senderLabelRead
  }

  const notificationContentStyle = isUnreadNotificationContent
    ? styles.notificationContentUnread
    : styles.notificationContentRead

  return (
    <View style={styles.container}>
      <View
        style={styles.avatarWrapper}
        testID={`NotificationContent/AvatarWrapper/${notification.id}`}
      >
        {hasImage ? (
          <View
            style={styles.notificationImageContainer}
            testID={`NotificationContent/ImageContainer/${notification.id}`}
          >
            <LoadingFastImage
              source={{ uri: imageUrl }}
              style={styles.notificationImage as ImageStyle}
              resizeMode="contain"
              testID={`NotificationContent/Image/${notification.id}`}
            />
          </View>
        ) : (
          <NotificationSenderAvatar avatarKind={presentation.avatarKind} iconName={iconName} />
        )}
        {showUnreadDot && presentation.isUnread && (
          <View
            style={styles.unreadDot}
            testID={`NotificationInbox/UnreadDot/${notification.id}`}
          />
        )}
      </View>

      <View style={styles.notificationBody}>
        {presentation.showOfferTag && (
          <View style={styles.offerPillContainer}>
            <View style={styles.offerPill}>
              <Text type="tiny" lineSpacing="none" bold style={styles.offerPillText}>
                OFFER
              </Text>
            </View>
          </View>
        )}
        <View
          style={[
            styles.notificationHeader,
            !shouldShowTimestamp && styles.notificationHeaderNoTime,
          ]}
        >
          <View style={styles.senderRow}>
            <Text type="small" bold style={[styles.senderLabel, senderLabelStyle]}>
              {notification.title || presentation.senderLabel}
            </Text>
          </View>

          {shouldShowTimestamp && (
            <View style={styles.timestampRow}>
              <Text numberOfLines={1} type="small" lineSpacing="tight" style={styles.timestamp}>
                {timestamp}
              </Text>
            </View>
          )}
        </View>

        <Text type="regular" lineSpacing="tight" style={notificationContentStyle}>
          {notification.content}
        </Text>
      </View>
    </View>
  )
}

const themedStyles = StyleService.create({
  container: {
    flexDirection: 'row',
  },
  avatarWrapper: {
    alignItems: 'center',
    justifyContent: 'center',
    height: AVATAR_SIZE,
    width: AVATAR_SIZE,
    marginRight: 12,
  },
  notificationImage: {
    height: 32,
    width: 32,
  },
  notificationImageContainer: {
    alignItems: 'center',
    backgroundColor: 'theme.transparent',
    borderColor: 'theme.surface.base3',
    borderRadius: 20,
    borderWidth: 1,
    height: AVATAR_SIZE,
    justifyContent: 'center',
    width: AVATAR_SIZE,
  },
  unreadDot: {
    position: 'absolute',
    right: 1,
    top: 1,
    borderRadius: 6,
    borderWidth: 1.5,
    borderColor: 'theme.solid.white',
    height: 12,
    width: 12,
    backgroundColor: 'theme.like',
  },
  notificationBody: {
    flex: 1,
    minWidth: 0,
  },
  notificationHeader: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4,
  },
  notificationHeaderNoTime: {
    justifyContent: 'flex-start',
  },
  senderRow: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginRight: 8,
    minWidth: 0,
  },
  senderLabel: {
    flexShrink: 1,
  },
  senderLabelUnread: {
    color: 'theme.text.primary',
  },
  senderLabelRead: {
    color: 'theme.text.secondary',
  },
  offerPillContainer: {
    alignItems: 'flex-start',
    marginBottom: 6,
  },
  offerPill: {
    flexShrink: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    borderRadius: 10,
    paddingHorizontal: 8,
    height: 20,
    backgroundColor: 'theme.success.base',
  },
  offerPillText: {
    top: 1,
    flexShrink: 1,
    color: 'theme.solid.white',
  },
  timestampRow: {
    alignItems: 'center',
    flexDirection: 'row',
    flexShrink: 0,
    marginLeft: 8,
  },
  timestamp: {
    color: 'theme.text.tertiary',
  },
  notificationContentUnread: {
    color: 'theme.text.primary',
    marginBottom: 0,
  },
  notificationContentRead: {
    color: 'theme.text.secondary',
    marginBottom: 0,
  },
})
